/**
 * services/offersApi.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Offer negotiation methods.
 *
 * METHODS:
 *   createOffer(adId, offerData)       — make an offer on a listing
 *   getAdOffers(adId)                  — get all offers on a specific ad
 *   getMyOffers()                      — get offers the current user made
 *   getReceivedOffers()                — get offers received on user's listings
 *   respondToOffer(offerId, action)    — accept / reject / counter an offer
 *   getOfferMessages(offerId)          — fetch messages in an offer thread
 *   sendOfferMessage(offerId, message) — send a message in an offer thread
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { API_BASE_URL } from './apiUtils.js';

function _getToken() {
  return (
    localStorage.getItem('authToken')  ||
    localStorage.getItem('token')      ||
    localStorage.getItem('adminToken') ||
    localStorage.getItem('admin_token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('authToken') ||
    sessionStorage.getItem('token')    ||
    sessionStorage.getItem('adminToken')
  );
}

function _adminHeaders(token) {
  const t = token || _getToken();
  return { Authorization: `Token ${t}`, 'Content-Type': 'application/json' };
}

// ── Methods — exported as a plain object, spread into ApiService in api.js ────
export const methods = {
async createOffer(adId, offerData) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'Please log in to make an offer' };
      const res = await fetch(`${API_BASE_URL}/ads/${adId}/offers/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(offerData),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || err.detail || 'Failed to create offer' };
      }
      const data = await res.json();
      return { success: true, offer_id: data.offer_id, message: data.message || 'Offer sent successfully!', data };
    } catch (error) { return { success: false, error: error.message }; }
  },

  async getAdOffers(adId) {
    try {
      const token = this._getToken();
      if (!token) return [];
      const res = await fetch(`${API_BASE_URL}/ads/${adId}/offers/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (!res.ok) throw new Error('Failed to get offers');
      const data = await res.json();
      return Array.isArray(data) ? data : (data.offers || data.results || []);
    } catch { return []; }
  },

  async getMyOffers() {
    try {
      const token = this._getToken();
      if (!token) return [];
      const res = await fetch(`${API_BASE_URL}/offers/my-offers/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch offers');
      const data = await res.json();
      return Array.isArray(data) ? data : (data.offers || data.results || []);
    } catch { return []; }
  },

  async getReceivedOffers() {
    try {
      const token = this._getToken();
      if (!token) return [];
      const res = await fetch(`${API_BASE_URL}/offers/received/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch received offers');
      const data = await res.json();
      return Array.isArray(data) ? data : (data.offers || data.results || []);
    } catch { return []; }
  },

  async respondToOffer(offerId, action) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const actionStr = typeof action === 'string' ? action : action?.action;
      const res = await fetch(`${API_BASE_URL}/offers/${offerId}/respond/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: actionStr }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || `Failed to ${actionStr} offer` };
      }
      const data = await res.json();
      return { success: true, message: data.message || `Offer ${actionStr}ed successfully!` };
    } catch (error) { return { success: false, error: error.message }; }
  },

  async getOfferMessages(offerId) {
    try {
      const token = this._getToken();
      if (!token) return [];
      const res = await fetch(`${API_BASE_URL}/offers/${offerId}/messages/`, {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch messages');
      const data = await res.json();
      return Array.isArray(data) ? data : (data.messages || data.results || []);
    } catch { return []; }
  },

  async sendOfferMessage(offerId, message) {
    try {
      const token = this._getToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const res = await fetch(`${API_BASE_URL}/offers/${offerId}/messages/send/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        return { success: false, error: err.error || 'Failed to send message' };
      }
      return { success: true, data: await res.json() };
    } catch (error) { return { success: false, error: error.message }; }
  }

  // MESSAGES — DIRECT AD MESSAGES
};

